import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getFirestore,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import {
  APPLICATION_STATUSES,
  ApplicationTimelineEntry,
  buildApplicantKey,
  CAREER_SOURCE,
  formatTimelineLabel,
  normalizeApplicationStatus,
} from '../utils/careerApplications';

// ─── Types (unchanged) ────────────────────────────────────────────────────────

type ApplicationRow = {
  id: string;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  enquiryType?: string | null;
  notes?: string | string[] | null;
  message?: string | null;
  submittedAt?: Timestamp | string | null;
  createdAt?: Timestamp | string | null;
  applicationStatus?: string | null;
  applicationDecisionNote?: string | null;
  applicationDecisionNoteUpdatedAt?: Timestamp | string | null;
  applicationStatusUpdatedAt?: Timestamp | string | null;
  applicationStatusUpdatedBy?: string | null;
  resumeUrl?: string | null;
  source?: string | null;
  normalizedPhone?: string | null;
  normalizedEmail?: string | null;
  applicantKey?: string | null;
  applicationTimeline?: ApplicationTimelineEntry[] | null;
};

const ROLE_FILTERS = [
  'All',
  'Sales Executive',
  'Civil Site Engineer',
  'Site Supervisor',
  'Intern',
] as const;

// ─── Helpers (unchanged) ──────────────────────────────────────────────────────

function toDateMaybe(value: unknown): Date | null {
  if (!value) return null;
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    const date = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

function normalizeRole(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function matchesRoleFilter(role: string, filter: string): boolean {
  if (filter === 'All') return true;
  const normalizedRole = normalizeRole(role);
  if (filter === 'Civil Site Engineer') {
    return (
      normalizedRole === 'civil site engineer' ||
      normalizedRole === 'civil engineer'
    );
  }
  return normalizedRole === normalizeRole(filter);
}

function roleChipStyles(role: string) {
  const normalized = normalizeRole(role);
  if (normalized.includes('sales'))
    return { bgcolor: 'rgba(157,111,50,0.10)', color: '#7a5526', borderColor: 'rgba(157,111,50,0.22)' };
  if (normalized.includes('civil'))
    return { bgcolor: 'rgba(76,145,88,0.12)', color: '#2d6b3c', borderColor: 'rgba(76,145,88,0.22)' };
  if (normalized.includes('site supervisor'))
    return { bgcolor: 'rgba(51,92,173,0.10)', color: '#24488e', borderColor: 'rgba(51,92,173,0.20)' };
  if (normalized.includes('intern'))
    return { bgcolor: 'rgba(114,72,167,0.10)', color: '#5c2f93', borderColor: 'rgba(114,72,167,0.20)' };
  return { bgcolor: 'rgba(26,26,26,0.06)', color: '#1a1a1a', borderColor: 'rgba(26,26,26,0.12)' };
}

function applicationStatusStyles(status: string) {
  if (status === 'Shortlisted')
    return { bgcolor: 'rgba(76,145,88,0.12)', color: '#2d6b3c', borderColor: 'rgba(76,145,88,0.22)' };
  if (status === 'Rejected')
    return { bgcolor: 'rgba(173,67,67,0.10)', color: '#983838', borderColor: 'rgba(173,67,67,0.18)' };
  if (status === 'Reviewed')
    return { bgcolor: 'rgba(100,116,139,0.10)', color: '#475569', borderColor: 'rgba(100,116,139,0.18)' };
  // New — gold
  return { bgcolor: 'rgba(157,111,50,0.10)', color: '#9d6f32', borderColor: 'rgba(157,111,50,0.24)' };
}

function formatTimelineDate(value: unknown): string {
  const date = toDateMaybe(value);
  if (!date) return '—';
  return `${date.toLocaleDateString()} · ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

function getApplicationKey(row: ApplicationRow): string | null {
  return row.applicantKey || buildApplicantKey(row.phone, row.email);
}

function getApplicationTimeline(row: ApplicationRow): ApplicationTimelineEntry[] {
  const appliedAt = toDateMaybe(row.submittedAt) || toDateMaybe(row.createdAt);
  const message = String(
    Array.isArray(row.notes) ? row.notes.join('\n') : row.notes || row.message || ''
  ).trim();
  const entries: ApplicationTimelineEntry[] = [
    {
      type: 'application-created',
      status: row.applicationStatus,
      role: row.enquiryType || null,
      note: message || null,
      byEmail: null,
      at: appliedAt,
    },
  ];
  if (Array.isArray(row.applicationTimeline)) {
    entries.push(
      ...row.applicationTimeline.filter(
        (entry) => String(entry?.type || '') !== 'application-created'
      )
    );
  }
  return entries.sort((a, b) => {
    const aTime = (toDateMaybe(a.at) || new Date(0)).getTime();
    const bTime = (toDateMaybe(b.at) || new Date(0)).getTime();
    return bTime - aTime;
  });
}

// ─── Design tokens ────────────────────────────────────────────────────────────

const GOLD   = '#9d6f32';
const DARK   = '#1a1a1a';
const MUTED  = '#6b6b6b';
const CREAM  = '#f9f7f5';
const CREAM2 = '#fdf9f5';
const BORDER = '#e8e8e8';

// Heading font matches the website; body uses Inter for readability in dense UI
const FONT_HEADING = "'Georgia', 'Times New Roman', serif";
const FONT_BODY    = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const selectSx = {
  borderRadius: 2,
  bgcolor: 'white',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
};

const labelSx = {
  '&.Mui-focused': { color: GOLD },
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function JobApplicationsPageV2() {
  const db = useMemo(() => getFirestore(), []);
  const { currentUser, roleId, userRole } = useAuth();
  const [applications, setApplications]       = useState<ApplicationRow[]>([]);
  const [roleFilter, setRoleFilter]           = useState<string>('All');
  const [statusFilter, setStatusFilter]       = useState<string>('All');
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<ApplicationRow | null>(null);
  const [statusDraft, setStatusDraft]         = useState<string>('New');
  const [decisionNoteDraft, setDecisionNoteDraft] = useState('');
  const [savingStatus, setSavingStatus]       = useState(false);
  const [deletingApplication, setDeletingApplication] = useState(false);

  const canDeleteApplications = useMemo(() => {
    const role = String(roleId || userRole || '').trim().toLowerCase();
    return role === 'admin' || role === 'super-admin' || role === 'superadmin';
  }, [roleId, userRole]);

  // ── Data (unchanged) ──────────────────────────────────────────────────────
  useEffect(() => {
    const applicationsQuery = query(
      collection(db, 'leads'),
      where('source', '==', CAREER_SOURCE)
    );
    const unsub = onSnapshot(
      applicationsQuery,
      (snapshot) => {
        const next = snapshot.docs
          .map((docSnap) => ({ ...(docSnap.data() as ApplicationRow), id: docSnap.id }))
          .sort((a, b) => {
            const aTime = (toDateMaybe(a.submittedAt) || toDateMaybe(a.createdAt) || new Date(0)).getTime();
            const bTime = (toDateMaybe(b.submittedAt) || toDateMaybe(b.createdAt) || new Date(0)).getTime();
            return bTime - aTime;
          });
        setApplications(next);
        setLoading(false);
      },
      (err) => {
        const message = typeof err?.message === 'string' && err.message.trim()
          ? err.message.trim() : 'Failed to load job applications.';
        setError(message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [db]);

  useEffect(() => {
    setStatusDraft(normalizeApplicationStatus(selectedApplication?.applicationStatus));
    setDecisionNoteDraft(String(selectedApplication?.applicationDecisionNote || ''));
  }, [selectedApplication]);

  const filteredApplications = useMemo(() =>
    applications.filter((row) => {
      const role   = String(row.enquiryType || '').trim();
      const status = normalizeApplicationStatus(row.applicationStatus);
      return matchesRoleFilter(role, roleFilter) && (statusFilter === 'All' || status === statusFilter);
    }),
  [applications, roleFilter, statusFilter]);

  const stats = useMemo(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - (day === 0 ? 6 : day - 1));
    startOfWeek.setHours(0, 0, 0, 0);
    return applications.reduce(
      (acc, row) => {
        const status    = normalizeApplicationStatus(row.applicationStatus);
        const appliedAt = toDateMaybe(row.submittedAt) || toDateMaybe(row.createdAt);
        acc.total += 1;
        if (status === 'New')         acc.newCount += 1;
        if (status === 'Shortlisted') acc.shortlisted += 1;
        if (appliedAt && appliedAt >= startOfWeek) acc.thisWeek += 1;
        return acc;
      },
      { total: 0, newCount: 0, shortlisted: 0, thisWeek: 0 }
    );
  }, [applications]);

  const applicantHistory = useMemo(() => {
    if (!selectedApplication) return [];
    const applicantKey = getApplicationKey(selectedApplication);
    if (!applicantKey) return [];
    return applications.filter((row) => getApplicationKey(row) === applicantKey);
  }, [applications, selectedApplication]);

  const handleSaveStatus = async () => {
    if (!selectedApplication) return;
    setSavingStatus(true);
    setError(null);
    try {
      const applicationRef  = doc(db, 'leads', selectedApplication.id);
      const trimmedNote     = decisionNoteDraft.trim();
      const previousStatus  = normalizeApplicationStatus(selectedApplication.applicationStatus);
      const timelineEntry: ApplicationTimelineEntry = {
        type: 'status-updated',
        previousStatus,
        nextStatus: statusDraft,
        status: statusDraft,
        note: trimmedNote || null,
        byEmail: currentUser?.email || null,
        at: Timestamp.now(),
      };
      await updateDoc(applicationRef, {
        applicationStatus: statusDraft,
        applicationStatusUpdatedAt: serverTimestamp(),
        applicationStatusUpdatedBy: currentUser?.email || null,
        applicationDecisionNote: trimmedNote || null,
        applicationDecisionNoteUpdatedAt: serverTimestamp(),
        applicationTimeline: arrayUnion(timelineEntry),
      });
      setSelectedApplication((prev) =>
        prev ? {
          ...prev,
          applicationStatus: statusDraft,
          applicationStatusUpdatedBy: currentUser?.email || null,
          applicationStatusUpdatedAt: Timestamp.now(),
          applicationDecisionNote: trimmedNote || null,
          applicationDecisionNoteUpdatedAt: Timestamp.now(),
          applicationTimeline: [
            ...(Array.isArray(prev.applicationTimeline) ? prev.applicationTimeline : []),
            timelineEntry,
          ],
        } : null
      );
    } catch (err) {
      console.error('Failed to save application status', err);
      setError('Failed to save application status.');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleDeleteApplication = async () => {
    if (!selectedApplication || !canDeleteApplications) return;
    const applicantName = String(selectedApplication.name || 'this application').trim();
    const confirmed = window.confirm(`Delete ${applicantName}? This cannot be undone.`);
    if (!confirmed) return;

    setDeletingApplication(true);
    setError(null);
    try {
      await deleteDoc(doc(db, 'leads', selectedApplication.id));
      setSelectedApplication(null);
    } catch (err) {
      console.error('Failed to delete job application', err);
      setError('Failed to delete job application.');
    } finally {
      setDeletingApplication(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: CREAM, minHeight: '100%' }}>

      {/* ── Header ── */}
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', lg: 'flex-end' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.24em',
              textTransform: 'uppercase',
              color: GOLD,
              mb: 0.5,
              fontFamily: FONT_BODY,
            }}
          >
            HR · Recruitment
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 26, sm: 32 },
              fontWeight: 800,
              color: DARK,
              fontFamily: FONT_HEADING,
              lineHeight: 1.1,
            }}
          >
            Job Applications
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: MUTED, fontFamily: FONT_BODY }}>
            {stats.total} total · {stats.newCount} unreviewed
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', lg: 'auto' } }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel sx={labelSx}>All roles</InputLabel>
            <Select
              value={roleFilter}
              label="All roles"
              onChange={(e) => setRoleFilter(String(e.target.value))}
              sx={selectSx}
            >
              {ROLE_FILTERS.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel sx={labelSx}>All statuses</InputLabel>
            <Select
              value={statusFilter}
              label="All statuses"
              onChange={(e) => setStatusFilter(String(e.target.value))}
              sx={selectSx}
            >
              <MenuItem value="All">All statuses</MenuItem>
              {APPLICATION_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}

      {/* ── Stat cards ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 1.5,
          mb: 3,
        }}
      >
        {[
          { label: 'Total',       value: stats.total,       highlight: false },
          { label: 'New',         value: stats.newCount,    highlight: true  },
          { label: 'Shortlisted', value: stats.shortlisted, highlight: false },
          { label: 'This Week',   value: stats.thisWeek,    highlight: false },
        ].map((card) => (
          <Box
            key={card.label}
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: card.highlight ? CREAM2 : 'white',
              border: `1px solid ${card.highlight ? 'rgba(157,111,50,0.30)' : BORDER}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.6rem',
                fontWeight: 700,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: card.highlight ? GOLD : MUTED,
                fontFamily: FONT_BODY,
              }}
            >
              {card.label}
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                fontSize: 34,
                fontWeight: 800,
                lineHeight: 1,
                color: card.highlight ? GOLD : DARK,
                fontFamily: FONT_HEADING,
              }}
            >
              {card.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── Table ── */}
      <Box
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'white',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: CREAM2 }}>
              {['Applicant', 'Phone', 'Role', 'Applied', 'Status', ''].map((col, i) => (
                <TableCell
                  key={col || i}
                  align={i === 5 ? 'right' : 'left'}
                  sx={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: GOLD,
                    borderBottom: `1px solid ${BORDER}`,
                    py: 1.5,
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: MUTED }}>
                  Loading applications…
                </TableCell>
              </TableRow>
            ) : filteredApplications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: MUTED }}>
                  No applications found.
                </TableCell>
              </TableRow>
            ) : (
              filteredApplications.map((row) => {
                const status       = normalizeApplicationStatus(row.applicationStatus);
                const appliedAt    = toDateMaybe(row.submittedAt) || toDateMaybe(row.createdAt);
                const applicantKey = getApplicationKey(row);
                const repeatCount  = applicantKey
                  ? applications.filter((c) => getApplicationKey(c) === applicantKey).length
                  : 1;

                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      bgcolor: status === 'New' ? 'rgba(157,111,50,0.03)' : 'transparent',
                      '&:hover': { bgcolor: 'rgba(157,111,50,0.05)' },
                      '&:last-child td': { borderBottom: 0 },
                    }}
                    onClick={() => setSelectedApplication(row)}
                  >
                    <TableCell sx={{ fontWeight: 600, color: DARK }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <span>{row.name || '—'}</span>
                        {repeatCount > 1 && (
                          <Chip
                            label={`${repeatCount}×`}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: 999,
                              bgcolor: 'rgba(157,111,50,0.08)',
                              color: '#7a5526',
                              borderColor: 'rgba(157,111,50,0.22)',
                              '& .MuiChip-label': { fontWeight: 700, fontSize: '0.65rem' },
                            }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ color: MUTED, fontSize: 13 }}>
                      {String(row.phone || '').replace(/^\+91\s?/, '') || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.enquiryType || '—'}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 999,
                          ...roleChipStyles(String(row.enquiryType || '')),
                          '& .MuiChip-label': { fontWeight: 700, fontSize: '0.7rem' },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: MUTED, fontSize: 13 }}>
                      {appliedAt ? appliedAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={status}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 999,
                          ...applicationStatusStyles(status),
                          '& .MuiChip-label': { fontWeight: 700, fontSize: '0.7rem' },
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                        onClick={(e) => { e.stopPropagation(); setSelectedApplication(row); }}
                        sx={{
                          borderRadius: 999,
                          borderColor: BORDER,
                          color: DARK,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': { borderColor: GOLD, color: GOLD, bgcolor: 'rgba(157,111,50,0.04)' },
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>

      {/* ── Detail Drawer ── */}
      <Drawer
        anchor="right"
        open={Boolean(selectedApplication)}
        onClose={() => setSelectedApplication(null)}
        sx={{ zIndex: 1300 }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 540 },
            maxWidth: '100%',
            bgcolor: CREAM,
            borderLeft: `1px solid ${BORDER}`,
          },
        }}
      >
        {selectedApplication && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

            {/* Unified drawer header — always visible on all screen sizes */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1.75,
                bgcolor: 'white',
                borderBottom: `1px solid ${BORDER}`,
                gap: 1.5,
                flexShrink: 0,
              }}
            >
              {/* Back / close button — prominent on mobile, subtle on desktop */}
              <Button
                onClick={() => setSelectedApplication(null)}
                startIcon={<ArrowBackIcon />}
                size="small"
                sx={{
                  borderRadius: 999,
                  border: `1px solid ${BORDER}`,
                  color: DARK,
                  fontFamily: FONT_BODY,
                  fontWeight: 600,
                  fontSize: 12,
                  textTransform: 'none',
                  px: 1.5,
                  py: 0.75,
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  '&:hover': { borderColor: GOLD, color: GOLD, bgcolor: 'rgba(157,111,50,0.04)' },
                }}
              >
                Back
              </Button>

              {/* Applicant name + role */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: DARK,
                    fontSize: 14,
                    fontFamily: FONT_BODY,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedApplication.name || 'Applicant'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: MUTED, fontFamily: FONT_BODY }}>
                  {selectedApplication.enquiryType || 'Role not set'}
                </Typography>
              </Box>

              {/* × close — secondary action, always visible */}
              <IconButton
                size="small"
                onClick={() => setSelectedApplication(null)}
                sx={{ color: MUTED, flexShrink: 0, '&:hover': { color: DARK } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Drawer body (scrollable) */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              <Stack spacing={2.5}>

                {/* Info grid */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 2,
                    p: 2.5,
                    bgcolor: 'white',
                    border: `1px solid ${BORDER}`,
                    borderRadius: 2.5,
                  }}
                >
                  {[
                    { label: 'Role',    value: selectedApplication.enquiryType },
                    { label: 'Phone',   value: selectedApplication.phone },
                    { label: 'Email',   value: selectedApplication.email },
                    {
                      label: 'Applied',
                      value: (toDateMaybe(selectedApplication.submittedAt) || toDateMaybe(selectedApplication.createdAt))
                        ?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
                    },
                  ].map(({ label, value }) => (
                    <Box key={label}>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, mb: 0.5, fontFamily: FONT_BODY }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontWeight: 600, color: DARK, fontSize: 13, fontFamily: FONT_BODY }}>
                        {value || '—'}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Cover note */}
                <Box>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, mb: 1, fontFamily: FONT_BODY }}>
                    Cover Note
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'white',
                      border: `1px solid ${BORDER}`,
                      borderRadius: 2.5,
                      color: MUTED,
                      fontSize: 13,
                      lineHeight: 1.7,
                      whiteSpace: 'pre-wrap',
                      fontFamily: FONT_BODY,
                    }}
                  >
                    {String(
                      Array.isArray(selectedApplication.notes)
                        ? selectedApplication.notes.join('\n')
                        : selectedApplication.notes || selectedApplication.message || 'No cover note provided.'
                    )}
                  </Box>
                </Box>

                {/* Repeat applicant notice */}
                {applicantHistory.length > 1 && (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: CREAM2,
                      border: `1px solid rgba(157,111,50,0.22)`,
                      borderRadius: 2.5,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: GOLD, fontSize: 13, mb: 0.5, fontFamily: FONT_BODY }}>
                      Repeat applicant
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: MUTED, fontFamily: FONT_BODY }}>
                      {applicantHistory.length} applications found for this phone / email.
                    </Typography>
                  </Box>
                )}

                {/* Resume */}
                {selectedApplication.resumeUrl ? (
                  <Button
                    variant="outlined"
                    href={selectedApplication.resumeUrl}
                    target="_blank"
                    rel="noreferrer"
                    startIcon={<DownloadOutlinedIcon />}
                    sx={{
                      alignSelf: 'flex-start',
                      borderRadius: 999,
                      borderColor: GOLD,
                      color: GOLD,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'rgba(157,111,50,0.06)', borderColor: '#7a5526' },
                    }}
                  >
                    Download Resume
                  </Button>
                ) : (
                  <Typography sx={{ fontSize: 13, color: MUTED, fontStyle: 'italic', fontFamily: FONT_BODY }}>
                    No resume provided
                  </Typography>
                )}

                {/* Timeline */}
                <Box>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, mb: 1.5, fontFamily: FONT_BODY }}>
                    Applicant Timeline
                  </Typography>
                  <Stack spacing={1}>
                    {applicantHistory.length === 0 ? (
                      <Typography sx={{ fontSize: 13, color: MUTED, fontFamily: FONT_BODY }}>No history found.</Typography>
                    ) : (
                      applicantHistory
                        .sort((a, b) => {
                          const aTime = (toDateMaybe(a.submittedAt) || toDateMaybe(a.createdAt) || new Date(0)).getTime();
                          const bTime = (toDateMaybe(b.submittedAt) || toDateMaybe(b.createdAt) || new Date(0)).getTime();
                          return bTime - aTime;
                        })
                        .flatMap((application) =>
                          getApplicationTimeline(application).map((entry, index) => (
                            <Box
                              key={`${application.id}-${String(entry.type || 'entry')}-${index}`}
                              sx={{
                                p: 1.75,
                                bgcolor: 'white',
                                border: `1px solid ${application.id === selectedApplication.id ? 'rgba(157,111,50,0.20)' : BORDER}`,
                                borderLeft: `3px solid ${application.id === selectedApplication.id ? GOLD : 'transparent'}`,
                                borderRadius: 2,
                              }}
                            >
                              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.5}>
                                <Box>
                                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: DARK, fontFamily: FONT_BODY }}>
                                    {formatTimelineLabel(entry)}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, color: MUTED, fontFamily: FONT_BODY }}>
                                    {application.name || 'Applicant'} · {application.enquiryType || 'Role not set'}
                                  </Typography>
                                </Box>
                                <Typography sx={{ fontSize: 11, color: MUTED, whiteSpace: 'nowrap', fontFamily: FONT_BODY }}>
                                  {formatTimelineDate(entry.at)}
                                </Typography>
                              </Stack>
                              {(entry.note || entry.byEmail) && (
                                <Box sx={{ mt: 0.75 }}>
                                  {entry.note && (
                                    <Typography sx={{ fontSize: 12, color: MUTED, fontFamily: FONT_BODY }}>{entry.note}</Typography>
                                  )}
                                  {entry.byEmail && (
                                    <Typography sx={{ fontSize: 11, color: MUTED, display: 'block', mt: 0.25, fontFamily: FONT_BODY }}>
                                      Updated by {entry.byEmail}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          ))
                        )
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Box>

            {/* Drawer footer — status update */}
            <Box
              sx={{
                p: 2.5,
                bgcolor: 'white',
                borderTop: `1px solid ${BORDER}`,
              }}
            >
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, mb: 1.5, fontFamily: FONT_BODY }}>
                Update Status
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="Decision note"
                  placeholder="Why shortlisted, rejected, or reviewed…"
                  value={decisionNoteDraft}
                  onChange={(e) => setDecisionNoteDraft(e.target.value)}
                  multiline
                  minRows={2}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: CREAM,
                      '& fieldset': { borderColor: BORDER },
                      '&:hover fieldset': { borderColor: GOLD },
                      '&.Mui-focused fieldset': { borderColor: GOLD },
                    },
                    '& label.Mui-focused': { color: GOLD },
                  }}
                />
                <Stack direction="row" spacing={1}>
                  {canDeleteApplications && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleDeleteApplication}
                      disabled={savingStatus || deletingApplication}
                      sx={{
                        minWidth: 120,
                        borderRadius: 2,
                        fontWeight: 600,
                        textTransform: 'none',
                      }}
                    >
                      {deletingApplication ? 'Deleting…' : 'Delete'}
                    </Button>
                  )}
                  <FormControl fullWidth size="small">
                    <InputLabel sx={labelSx}>Status</InputLabel>
                    <Select
                      value={statusDraft}
                      label="Status"
                      onChange={(e) => setStatusDraft(String(e.target.value))}
                      sx={selectSx}
                    >
                      {APPLICATION_STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    onClick={handleSaveStatus}
                    disabled={savingStatus || deletingApplication}
                    sx={{
                      minWidth: 130,
                      borderRadius: 2,
                      bgcolor: GOLD,
                      color: 'white',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#7a5526', boxShadow: 'none' },
                      '&:disabled': { bgcolor: 'rgba(157,111,50,0.4)', color: 'white' },
                    }}
                  >
                    {savingStatus ? 'Saving…' : 'Save status'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
